const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.citycourier.org/api/v1';

export interface JobPosting {
    id: number;
    title: string;
    description: string;
    location?: string;
    job_type?: string;
    required_experience?: string;
    salary?: string;
    requirements?: string;
    responsibilities?: string;
    benefits?: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    careers_count?: number;
}

export interface CreateJobPostingData {
    title: string;
    description: string;
    location?: string;
    job_type?: string;
    required_experience?: string;
    salary?: string;
    requirements?: string;
    responsibilities?: string;
    benefits?: string;
    is_active?: boolean;
}

export interface UpdateJobPostingData extends CreateJobPostingData {}

export interface JobPostingFilters {
    search?: string;
    status?: 'active' | 'inactive' | '';
    page?: number;
    per_page?: number;
}

export interface JobPostingResponse {
    job_postings: JobPosting[];
    pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
    };
}

export interface BackendResponse<T> {
    success: boolean;
    data: T;
    message: string;
}

// Helper function to get auth token
const getAuthToken = (): string | null => {
    return localStorage.getItem('token');
};

// Helper function to handle API errors
const handleApiError = async (response: Response): Promise<never> => {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
};

export const jobPostingApi = {
    // Get all job postings (admin)
    getJobPostings: async (filters: JobPostingFilters = {}): Promise<JobPostingResponse> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const params = new URLSearchParams();
        
        if (filters.search) params.append('search', filters.search);
        if (filters.status) params.append('status', filters.status);
        if (filters.page) params.append('page', filters.page.toString());
        if (filters.per_page) params.append('per_page', filters.per_page.toString());

        const response = await fetch(`${API_BASE_URL}/admin/jobs?${params.toString()}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<JobPostingResponse> = await response.json();
        return data.data;
    },

    // Get a specific job posting
    getJobPosting: async (id: number): Promise<JobPosting> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/admin/jobs/${id}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<JobPosting> = await response.json();
        return data.data;
    },

    // Create a new job posting
    createJobPosting: async (data: CreateJobPostingData): Promise<JobPosting> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/admin/jobs`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const responseData: BackendResponse<JobPosting> = await response.json();
        return responseData.data;
    },

    // Update a job posting
    updateJobPosting: async (id: number, data: UpdateJobPostingData): Promise<JobPosting> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/admin/jobs/${id}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const responseData: BackendResponse<JobPosting> = await response.json();
        return responseData.data;
    },

    // Delete a job posting
    deleteJobPosting: async (id: number): Promise<void> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/admin/jobs/${id}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }
    },

    // Toggle job posting status
    toggleJobPostingStatus: async (id: number): Promise<JobPosting> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/admin/jobs/${id}/toggle-status`, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<JobPosting> = await response.json();
        return data.data;
    },

    // Get job applications for a specific job posting
    getJobApplications: async (id: number): Promise<any[]> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/admin/jobs/${id}/applications`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<any[]> = await response.json();
        return data.data;
    },

    // Download resume
    downloadResume: async (careerId: number): Promise<Blob> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/admin/resume/${careerId}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        return response.blob();
    },

    // Public APIs
    // Get all active job postings (public)
    getPublicJobPostings: async (filters: { location?: string; job_type?: string } = {}): Promise<JobPosting[]> => {
        const params = new URLSearchParams();
        
        if (filters.location) params.append('location', filters.location);
        if (filters.job_type) params.append('job_type', filters.job_type);

        const response = await fetch(`${API_BASE_URL}/jobs?${params.toString()}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<JobPosting[]> = await response.json();
        return data.data;
    },

    // Get a specific job posting (public)
    getPublicJobPosting: async (id: number): Promise<JobPosting> => {
        const response = await fetch(`${API_BASE_URL}/jobs/${id}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<JobPosting> = await response.json();
        return data.data;
    },

    // Submit job application (public)
    applyForJob: async (data: {
        job_posting_id: number;
        name: string;
        email: string;
        phone: string;
        designation: string;
        resume: string; // base64 or file path
    }): Promise<any> => {
        const response = await fetch(`${API_BASE_URL}/jobs/apply`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const responseData: BackendResponse<any> = await response.json();
        return responseData.data;
    }
};
